import React, { useCallback, useEffect, useState } from 'react'
import { CAlert, CButton, CCard, CCardBody, CCol, CRow } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilCheckCircle,
  cilClock,
  cilListNumbered,
  cilReload,
  cilStar,
  cilWarning,
} from '@coreui/icons'
import CommonService from 'src/service/CommonService'
import GeneralService from 'src/service/GeneralService'
import ToastComponent from 'src/components/common/TaostComponent'
import ActivateTotr from './ActivateTotr'
import TeamOfTheWeekTable from './TeamOfTheWeekTable'
import { isSetUp } from './totrUtils'
import './TeamOfTheWeek.css'

const TeamOfTheRound = () => {
  const [active, setActive] = useState(null)
  const [activeError, setActiveError] = useState(false)
  const [toggling, setToggling] = useState(false)
  const [rounds, setRounds] = useState([])
  const [roundsLoading, setRoundsLoading] = useState(true)
  const [roundsError, setRoundsError] = useState(false)

  const loadStatus = useCallback(() => {
    setActiveError(false)
    GeneralService.getUserGameInfo()
      .then((res) => {
        if (res.status === 200) setActive(!!res.data?.is_totr_activate)
        else setActiveError(true)
      })
      .catch(() => setActiveError(true))
  }, [])

  const fetchData = useCallback(() => {
    setRoundsLoading(true)
    setRoundsError(false)
    CommonService.getTotwData()
      .then((result) => setRounds(result.data || []))
      .catch(() => setRoundsError(true))
      .finally(() => setRoundsLoading(false))
  }, [])

  useEffect(() => {
    loadStatus()
    fetchData()
  }, [loadStatus, fetchData])

  const handleToggle = (checked) => {
    setActive(checked)
    setToggling(true)
    GeneralService.changeUserGameAddOnStatus({
      addOnType: 'is_totr_activate',
      status: checked,
    })
      .then((res) => {
        if (res.status === 200) {
          ToastComponent(res.message, 'success')
        } else {
          setActive(!checked)
          ToastComponent(res.message, 'error')
        }
      })
      .catch((e) => {
        // Put the switch back - the saved status did not change.
        setActive(!checked)
        ToastComponent(e.response?.data?.message || 'Could not change the status', 'error')
      })
      .finally(() => setToggling(false))
  }

  const setUpCount = rounds.filter(isSetUp).length

  const STAT_CARDS = [
    { label: 'Status', value: 'Active', icon: cilStar, color: 'success' },
    {
      label: 'Rounds',
      value: roundsLoading ? '—' : rounds.length,
      icon: cilListNumbered,
      color: 'primary',
    },
    {
      label: 'Teams Set Up',
      value: roundsLoading ? '—' : setUpCount,
      icon: cilCheckCircle,
      color: 'info',
    },
    {
      label: 'Not Set Up',
      value: roundsLoading ? '—' : rounds.length - setUpCount,
      icon: cilClock,
      color: 'warning',
    },
  ]

  return (
    <>
      <div className="ge-page-header">
        <h1 className="ge-page-title">Team of the Round</h1>
        <p className="ge-page-subtitle">
          Turn Team of the Round on, pick each round&apos;s standout team, and award its players
          bonus points.
        </p>
      </div>

      {activeError && (
        <CAlert color="danger" className="d-flex flex-wrap align-items-center gap-2">
          <CIcon icon={cilWarning} className="flex-shrink-0" />
          <span>
            We couldn&apos;t load whether Team of the Round is on. Nothing has been changed.
          </span>
          <CButton
            color="danger"
            variant="outline"
            size="sm"
            className="ms-auto"
            onClick={loadStatus}
          >
            <CIcon icon={cilReload} className="me-1" />
            Retry
          </CButton>
        </CAlert>
      )}

      {active && (
        <CRow className="g-3 mb-3">
          {STAT_CARDS.map((s) => (
            <CCol sm={6} xl={3} key={s.label}>
              <CCard className="h-100">
                <CCardBody className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-medium-emphasis small">{s.label}</div>
                    <div className="fs-4 fw-semibold">{s.value}</div>
                  </div>
                  <div className={`ge-stat-icon ge-stat-icon--${s.color}`}>
                    <CIcon icon={s.icon} size="lg" />
                  </div>
                </CCardBody>
              </CCard>
            </CCol>
          ))}
        </CRow>
      )}

      <CRow className="g-3">
        {!activeError && (
          <CCol xs={12}>
            <ActivateTotr
              active={active}
              loading={active === null}
              toggling={toggling}
              onToggle={handleToggle}
            />
          </CCol>
        )}

        {active && (
          <CCol xs={12}>
            <TeamOfTheWeekTable
              rounds={rounds}
              loading={roundsLoading}
              error={roundsError}
              fetchData={fetchData}
            />
          </CCol>
        )}
      </CRow>
    </>
  )
}

export default TeamOfTheRound
